import { useAuth } from "react-oidc-context";
import { useActiveOrg } from "common-fe";
import { jwtDecode } from "jwt-decode";

const parseMap = (claim) => {
  if (!claim) return {};
  if (typeof claim === "object") return claim;
  try {
    return JSON.parse(claim);
  } catch {
    return {};
  }
};

/**
 * The current user's governance standing in the ACTIVE org, derived from the
 * token's per-org roles map + the membership owner flag. This is what gates the
 * org-admin affordances (and fixes NOTES #1: an org_admin who isn't an owner
 * must still see member-management controls).
 *
 *  - owner: rename/delete the org, make other owners (owner-only).
 *  - admin (or owner): manage members (add/remove/assign roles).
 *
 * The API re-checks server-side regardless of what the UI shows.
 */
export function useActiveOrgRole() {
  const auth = useAuth();
  const { activeOrgId, orgs, isSuper } = useActiveOrg();
  const activeOrg = orgs.find((o) => o.id === activeOrgId) || null;

  let roles = [];
  try {
    if (auth.user?.access_token) {
      roles = parseMap(jwtDecode(auth.user.access_token).roles)[activeOrgId] || [];
    }
  } catch {
    /* keep default */
  }

  const isOwner =
    isSuper || activeOrg?.owner === true || roles.includes("account:org_owner");
  const isAdmin = roles.includes("account:org_admin");

  return {
    activeOrgId,
    activeOrg,
    isSuper,
    isOwner,
    isAdmin,
    canManageMembers: isOwner || isAdmin,
  };
}
